import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useLang } from "../context/LangContext";

const COPY = {
  fr: { badge: "Témoignage vidéo", role: "Directeur · Port de Nice", caption: "Son retour après une séance Riviera Shockwaves.", duration: "1 min 08" },
  en: { badge: "Video testimonial", role: "Director · Port de Nice", caption: "His feedback after a Riviera Shockwaves session.", duration: "1 min 08" },
  it: { badge: "Testimonianza video", role: "Direttore · Port de Nice", caption: "Il suo feedback dopo una sessione Riviera Shockwaves.", duration: "1 min 08" },
  ru: { badge: "Видеоотзыв", role: "Директор · Порт Ниццы", caption: "Его отзыв после сеанса Riviera Shockwaves.", duration: "1 мин 08" },
};

export default function FeaturedTestimonial() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => setPlaying(false));
    setPlaying(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="mt-12 hairline bg-[var(--surface-1)] overflow-hidden"
      data-testid="featured-testimonial"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Portrait video */}
        <div className="lg:col-span-4 relative bg-[var(--ink)]">
          <div className="relative aspect-[478/850] lg:aspect-auto lg:h-full lg:min-h-[420px]">
            <video
              ref={videoRef}
              src="/videos/avis-alain-mistre.mp4"
              poster="/videos/avis-alain-mistre-poster.jpg"
              controls={playing}
              playsInline
              preload="metadata"
              onError={() => setPlaying(false)}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="featured-video"
            />
            {!playing && (
              <button
                onClick={play}
                className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/15 hover:bg-black/25 transition-colors cursor-pointer group"
                aria-label="Lire la vidéo"
                data-testid="featured-video-play"
              >
                <span className="w-16 h-16 rounded-full bg-[var(--gold)] text-white flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
                  <Play size={24} className="ml-1" fill="currentColor" />
                </span>
              </button>
            )}
          </div>
        </div>
        {/* Editorial side */}
        <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="signature-badge">{c.badge}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink)]/40 font-display">{c.duration}</span>
          </div>
          <h3 className="font-serif-luxe text-3xl md:text-4xl text-[var(--ink)] mt-6">Alain Mistre</h3>
          <div className="overline mt-2">{c.role}</div>
          <p className="mt-6 text-[15px] text-[var(--text-secondary)] font-body leading-relaxed max-w-xl">
            {c.caption}
          </p>
          <div className="mt-8 flex gap-1 text-[var(--gold)] text-lg" aria-label="5 étoiles">
            {[1, 2, 3, 4, 5].map((n) => <span key={n}>★</span>)}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
